import { useMemo, useState } from 'react';
import useApi from '../../../../../utils/AxiosWrapper';
import { useUser } from '../../../../../utils/UserContext';
import { ClipLoader } from 'react-spinners';
import { useScreenView } from '../../../../../utils/ScreenViewContext';
import { MonthlyStatement } from '../../../../../types/dbTypes';
import { DownloadSimple, Files } from '@phosphor-icons/react';
import { formatDate } from '../../../../../utils/DateUtils';
import { useQuery } from '@tanstack/react-query';
import { AccentButtonOutline } from '../../../../../shared/Buttons';
import { CustomerWithDriverOnboarding } from '../../../../../types/apiTypes';
import { isPreActiveCustomer } from '../home/utils/demoData';

interface MonthlyStatementsProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function MonthlyStatements({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
}: MonthlyStatementsProps) {
  const { screenView } = useScreenView();
  const { callProtectedApi } = useApi();
  const { userId, userRecord } = useUser();
  const [loadingMap, setLoadingMap] = useState<Map<string, boolean>>(new Map());
  const customerStatus = (userRecord as CustomerWithDriverOnboarding)?.status;
  const isPreActive = useMemo(
    () => isPreActiveCustomer(customerStatus),
    [customerStatus]
  );
  const { data: monthlyStatements = [], isLoading } = useQuery<
    MonthlyStatement[]
  >({
    queryKey: ['monthlyStatements', userId ?? ''],
    queryFn: async () => {
      const response = await callProtectedApi(
        `/monthlyStatements/${userId ?? ''}`,
        'get',
        {}
      );
      return response?.data?.data?.monthlyStatements || [];
    },
    enabled: !!userId && !isPreActive,
  });

  const downloadPdf = async (statementId: string) => {
    setLoadingMap((prev) => new Map(prev).set(statementId, true));
    try {
      const response = await callProtectedApi(
        `/documents/display/monthlyStatement/${statementId}`,
        'get',
        {},
        'Error occurred while downloading CSV'
      );
      const presigned_url = response?.data?.data?.presigned_url;
      if (presigned_url) {
        window.open(presigned_url, '_blank');
      }
    } catch (error: any) {
      console.error('Error occurred while downloading PDF: ', error);
    } finally {
      setLoadingMap((prev) => new Map(prev).set(statementId, false));
    }
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <>
            <div className="flex h-full flex-col overflow-x-hidden overflow-y-hidden sm:w-[95%] sm:pb-4 md:w-full md:px-4 lg:w-[1148px] lg:max-w-[1148px] lg:px-0 lg:pb-0">
              <div className="mt-[16px] h-full flex-grow-0 overflow-y-auto rounded-[4px] border-[1px] border-bordergray bg-white">
                {!isLoading ? (
                  <>
                    {monthlyStatements && monthlyStatements?.length > 0 ? (
                      <table className="w-full min-w-full table-fixed overflow-hidden">
                        <thead className="h-[50px] max-h-[50px] bg-primarybg">
                          <tr className="h-[50px] max-h-[50px] overflow-hidden">
                            <th className="h-[50px] max-h-[50px] overflow-hidden px-2 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary md:px-4">
                              Month
                            </th>
                            <th className="h-[50px] max-h-[50px] px-2 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary md:px-4">
                              Year
                            </th>
                            {screenView !== 'mobile' && (
                              <th className="h-[50px] max-h-[50px] px-2 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary md:px-4">
                                Created At
                              </th>
                            )}
                            <th className="h-[50px] max-h-[50px] px-2 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary sm:w-1/2 md:w-1/4 md:px-4"></th>
                          </tr>
                        </thead>
                        <tbody className="table-fixed overflow-hidden">
                          {monthlyStatements.map(
                            (
                              monthlyStatement: MonthlyStatement,
                              index: number
                            ) => (
                              <tr
                                key={index}
                                className="h-[50px] max-h-[50px] overflow-hidden border-b border-gray-200"
                              >
                                <td className="h-[50px] max-h-[50px] overflow-hidden px-2 py-2 text-[14px] text-primary md:px-4">
                                  {MONTHS[monthlyStatement?.month - 1]}
                                </td>
                                <td className="h-[50px] max-h-[50px] overflow-hidden px-2 py-2 text-[14px] text-primary md:px-4">
                                  {monthlyStatement?.year}
                                </td>
                                {screenView !== 'mobile' && (
                                  <td
                                    className={`h-[50px] max-h-[50px] px-2 py-2 text-[14px] sm:hidden md:table-cell md:px-4`}
                                  >
                                    {formatDate(
                                      new Date(
                                        monthlyStatement?.created_at ?? ''
                                      ),
                                      true
                                    )}
                                  </td>
                                )}
                                <td className="h-[50px] max-h-[50px] overflow-hidden px-2 py-2 text-[14px] text-primary sm:w-1/2 md:w-1/4 md:px-4">
                                  <div className="flex items-center justify-start space-x-2">
                                    <AccentButtonOutline
                                      onClick={async () => {
                                        await downloadPdf(
                                          monthlyStatement?.statement_id
                                        );
                                      }}
                                      disabled={
                                        !monthlyStatement?.statement_pdf_key ||
                                        monthlyStatement?.statement_pdf_key ===
                                          ''
                                      }
                                      addClass="px-3 py-[2px] rounded-[8px] flex items-center justify-center space-x-1 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white"
                                    >
                                      <DownloadSimple size={16} />{' '}
                                      <p>Download</p>
                                    </AccentButtonOutline>
                                    {loadingMap.get(
                                      monthlyStatement?.statement_id ?? ''
                                    ) && (
                                      <ClipLoader
                                        color="#1D213B"
                                        loading={loadingMap.get(
                                          monthlyStatement?.statement_id ?? ''
                                        )}
                                        size={20}
                                        aria-label="Loading Spinner"
                                        data-testid="loader"
                                        className="flex h-full w-full items-center justify-center"
                                      />
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    ) : (
                      <div className="flex h-full w-full flex-col items-center justify-center space-y-2">
                        <div className="flex h-[64px] w-[64px] items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-[#F5F6F7]">
                          <Files size={40} className="text-graysecondary" />
                        </div>
                        <p className="text-[14px] leading-[20px] text-graysecondary">
                          No Monthly Statements Found
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <ClipLoader
                      color="#1D213B"
                      loading={isLoading}
                      size={60}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="flex h-full w-full items-center justify-center"
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default MonthlyStatements;
